import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LinkedInLanding from "@/pages/LinkedInLanding";
import ServicePage from "@/pages/ServicePage";
import ServicesIndex from "@/pages/ServicesIndex";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import Quote from "@/pages/Quote";
import Admin from "@/pages/Admin";
import AuthCallback from "@/components/AuthCallback";
import { Privacy, Terms, Refunds, DataHandling, Contact } from "@/pages/Legal";
import { initAnalytics, captureUTM } from "@/lib/analytics";

function SmoothScroll() {
  useEffect(() => {
    initAnalytics();
    captureUTM();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.__lenis = null;
      lenis.destroy();
    };
  }, []);
  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        const el = document.querySelector(hash);
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (Math.abs(top - 80) < 150) {
            clearInterval(timer);
            return;
          }
          if (window.__lenis) {
            window.__lenis.resize();
            window.__lenis.scrollTo(el, { offset: -80, immediate: true });
          } else {
            el.scrollIntoView();
          }
        }
        if (attempts > 15) clearInterval(timer);
      }, 200);
      return () => clearInterval(timer);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Process the OAuth callback FIRST, before any route/auth check (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/linkedin" element={<LinkedInLanding />} />
      <Route path="/services" element={<ServicesIndex />} />
      <Route path="/services/:slug" element={<ServicePage />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/insights/:slug" element={<Article />} />
      <Route path="/quote/:token" element={<Quote />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/data-handling" element={<DataHandling />} />
      <Route path="/contact" element={<Contact />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <SmoothScroll />
        <ScrollManager />
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
